import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Receipt, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetFiscalConfig,
  useUpsertFiscalConfig,
  SECRET_KEEP_SENTINEL,
  type FiscalConfigInput,
  type TaxLabel,
} from '@/services/api/endpoints/CompanyConfigService';

const TAX_LABEL_VALUES: TaxLabel[] = ['Ђ', 'Е', 'Г', 'А', 'Ж'];

interface Props {
  companyId: string;
}

export function CompanyFiscalConfigTab({ companyId }: Props) {
  const { t } = useTranslation();
  const { data: config, isLoading } = useGetFiscalConfig(companyId);
  const upsert = useUpsertFiscalConfig(companyId);

  const [enabled, setEnabled] = useState(false);
  const [intervendApiKey, setIntervendApiKey] = useState('');
  const [intervendApiKeyIdentifier, setIntervendApiKeyIdentifier] = useState('');
  const [intervendBaseUrl, setIntervendBaseUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [topupItemName, setTopupItemName] = useState('VendPay Credits');
  const [topupTaxLabel, setTopupTaxLabel] = useState<TaxLabel>('Ђ');
  const [apiKeyTouched, setApiKeyTouched] = useState(false);
  const [webhookSecretTouched, setWebhookSecretTouched] = useState(false);

  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled);
    setIntervendApiKeyIdentifier(config.intervendApiKeyIdentifier ?? '');
    setIntervendBaseUrl(config.intervendBaseUrl ?? '');
    setTopupItemName(config.topupItemName);
    setTopupTaxLabel(config.topupTaxLabel);
    setIntervendApiKey('');
    setWebhookSecret('');
    setApiKeyTouched(false);
    setWebhookSecretTouched(false);
  }, [config]);

  const handleSave = async () => {
    if (!topupItemName.trim()) {
      toast.error(t('payments.topupItemNameRequired'));
      return;
    }

    const body: FiscalConfigInput = {
      enabled,
      intervendApiKey: apiKeyTouched ? intervendApiKey : SECRET_KEEP_SENTINEL,
      intervendApiKeyIdentifier: intervendApiKeyIdentifier.trim() || null,
      intervendBaseUrl: intervendBaseUrl.trim() || null,
      webhookSecret: webhookSecretTouched ? webhookSecret : SECRET_KEEP_SENTINEL,
      topupItemName: topupItemName.trim(),
      topupTaxLabel,
    };

    try {
      await upsert.mutateAsync(body);
      toast.success(t('payments.fiscalConfigSaved'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('payments.savingFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              {t('payments.fiscalConfigTitle')}
            </CardTitle>
            <CardDescription>{t('payments.fiscalConfigDescription')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-sm text-muted-foreground">
              {enabled ? t('common.active') : t('common.inactive')}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{t('payments.fields.topupItemName')}</Label>
            <Input
              value={topupItemName}
              onChange={(e) => setTopupItemName(e.target.value)}
              placeholder="VendPay Credits"
            />
            <p className="text-xs text-muted-foreground">{t('payments.fields.topupItemNameHelp')}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t('payments.fields.taxLabel')}</Label>
            <Select value={topupTaxLabel} onValueChange={(v) => setTopupTaxLabel(v as TaxLabel)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAX_LABEL_VALUES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`payments.taxLabels.${v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('payments.fields.intervendApiKeyIdentifier')}</Label>
            <Input
              value={intervendApiKeyIdentifier}
              onChange={(e) => setIntervendApiKeyIdentifier(e.target.value)}
              placeholder={t('payments.fields.apiKeyIdHint')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('payments.fields.intervendBaseUrl')}</Label>
            <Input
              value={intervendBaseUrl}
              onChange={(e) => setIntervendBaseUrl(e.target.value)}
              placeholder="https://api.intervend.rs/api/v3/fiscal"
            />
          </div>
        </div>

        <SecretField
          label={t('payments.fields.intervendApiKey')}
          status={config?.hasApiKey}
          value={intervendApiKey}
          touched={apiKeyTouched}
          onChange={(v) => {
            setIntervendApiKey(v);
            setApiKeyTouched(true);
          }}
        />
        <SecretField
          label={t('payments.fields.intervendWebhookSecret')}
          status={config?.hasWebhookSecret}
          value={webhookSecret}
          touched={webhookSecretTouched}
          onChange={(v) => {
            setWebhookSecret(v);
            setWebhookSecretTouched(true);
          }}
        />

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SecretField({
  label,
  status,
  value,
  touched,
  onChange,
}: {
  label: string;
  status: boolean | undefined;
  value: string;
  touched: boolean;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span
          className={`text-xs flex items-center gap-1 ${
            status ? 'text-green-600' : 'text-muted-foreground'
          }`}
        >
          {status ? (
            <>
              <Check className="w-3 h-3" /> {t('payments.secretConfigured')}
            </>
          ) : (
            <>
              <X className="w-3 h-3" /> {t('payments.secretNotSet')}
            </>
          )}
        </span>
      </div>
      <Input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={status && !touched ? t('payments.fields.secretKeep') : t('payments.fields.secretEnter')}
      />
      {touched && value === '' && (
        <p className="text-xs text-amber-600">{t('payments.fields.secretWillClear')}</p>
      )}
    </div>
  );
}
